import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Briefcase, AlertCircle, CheckCircle, ArrowRight, ArrowLeft,
  User, Mail, Phone, Building2, MapPin, Globe, Sparkles, UserPlus,
  Loader2, X, Users,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import UnifiedActivitySelector from '../components/UnifiedActivitySelector';
import PasswordWithStrength from '../components/PasswordWithStrength';
import LanguageSelector from '../components/LanguageSelector';
import AuthLayout from '../components/AuthLayout';

import {
  INDICATIFS_TELEPHONIQUES,
  PAYS_LIST,
  getIndicatifByCode,
  getPaysByCode,
  getFlagUrl,
  validatePhone,
} from '../config/membreFormConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RegisterPage() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nom: '',
    prenom: '',
    telephone: '',
    indicatif_pays: '+33',
    role: 'beneficiaire_pro',
    statut_juridique: undefined,
    code_naf: '',
    territoire: undefined,
    pays: 'FR',
    activite_professionnelle: null,
  });
  const [loading, setLoading] = useState(false);
  const [territoires, setTerritoires] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTerritoires = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/territoires`);
        if (response.ok) {
          const data = await response.json();
          setTerritoires(data);
        }
      } catch {
        // silent fail
      }
    };
    fetchTerritoires();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await register(formData);

    if (result.success) {
      toast.success(t('register.registration_success', 'Inscription réussie !'));
      navigate('/login');
    } else {
      toast.error(result.error);
    }

    setLoading(false);
  };

  const steps = [
    { id: 1, title: 'Identité', icon: User },
    { id: 2, title: 'Contact', icon: Mail },
    { id: 3, title: 'Profil', icon: Briefcase },
  ];

  return (
    <AuthLayout
      variant="default"
      badge="Créer un compte"
      badgeIcon={UserPlus}
      title="Rejoignez la"
      titleHighlight="communauté O'SCOP"
      description="Créez votre espace O’SCOP pour présenter vos besoins et être orienté vers les achats mutualisés, l’emploi partagé, la formation, la logistique ou les dispositifs solidaires."
      features={[
        { icon: Building2, label: 'Un compte professionnel ou particulier' },
        { icon: CheckCircle, label: 'Une inscription guidée en trois étapes' },
        { icon: Users, label: 'Une orientation vers les services O’SCOP' },
      ]}
    >
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
          data-testid="register-close-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Accueil
        </button>
        <LanguageSelector variant="compact" />
      </div>

      <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2">
        {t('register.account_heading', 'Créer votre compte O’SCOP')}
      </h2>
      <p className="text-slate-600 dark:text-slate-300 text-sm mb-5">
        {t('register.account_intro', 'Renseignez votre identité, vos coordonnées et votre profil pour démarrer.')}
      </p>
      <p className="mb-5 rounded-xl bg-slate-50 p-3 text-xs leading-relaxed text-slate-600 dark:bg-slate-800 dark:text-slate-300">
        La création du compte, l’adhésion à la FTPE et l’admission comme associé coopérateur
        sont des démarches distinctes. Chaque service conserve ses conditions d’accès.
        {' '}<Link to="/ftpe-outremer/pourquoi-adherer" className="font-medium underline">Comprendre l’adhésion FTPE</Link>.
      </p>

      {/* Progress Steps — version premium claire */}
      <div className="flex items-center justify-between mb-6" data-testid="register-steps">
        {steps.map((step, idx) => {
          const Icon = step.icon;
          const isActive = currentStep === step.id;
          const isDone = currentStep > step.id;
          return (
            <div key={step.id} className="flex items-center flex-1">
              <div
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold transition-all ${
                  isActive
                    ? 'bg-blue-600 border-blue-600 text-white shadow-md shadow-blue-600/20'
                    : isDone
                      ? 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300'
                      : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {step.title}
              </div>
              {idx < steps.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-2 transition-colors ${
                    isDone ? 'bg-emerald-400' : 'bg-slate-200 dark:bg-slate-700'
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
        {/* Step 1: Identity */}
        {currentStep === 1 && (
          <div className="space-y-4 animate-in fade-in duration-300">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="nom" className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                  {t('register.last_name', 'Nom')}
                </Label>
                <div className="relative mt-1.5">
                  <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="nom"
                    data-testid="register-nom-input"
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    placeholder="Votre nom"
                    className="pl-10 h-12"
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="prenom" className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                  {t('register.first_name', 'Prénom')}
                </Label>
                <div className="relative mt-1.5">
                  <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="prenom"
                    data-testid="register-prenom-input"
                    value={formData.prenom}
                    onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                    placeholder="Votre prénom"
                    className="pl-10 h-12"
                    required
                  />
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="role" className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                {t('register.account_type', 'Type de compte')}
              </Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger data-testid="register-role-select" className="h-12 mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="beneficiaire_pro">
                    {t('register.professional_beneficiary', 'Bénéficiaire professionnel')}
                  </SelectItem>
                  <SelectItem value="beneficiaire_particulier">
                    {t('register.individual_beneficiary', 'Bénéficiaire particulier')}
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {formData.role === 'beneficiaire_pro'
                  ? (t('register.for_professionals', 'Pour les entreprises et indépendants'))
                  : (t('register.for_individuals', 'Pour les particuliers'))}
              </p>
            </div>

            <Button
              type="button"
              onClick={() => setCurrentStep(2)}
              className="w-full h-12 text-base font-semibold bg-gradient-to-r from-[#0B3C5D] to-[#0a1e3f] hover:from-[#0a3252] hover:to-[#091a36] shadow-lg shadow-blue-900/30 hover:shadow-xl text-white"
              data-testid="register-step1-next-btn"
            >
              Continuer
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 2: Contact */}
        {currentStep === 2 && (
          <div className="space-y-4 animate-in fade-in duration-300">
            <div>
              <Label htmlFor="email" className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                {t('register.email', 'Email')}
              </Label>
              <div className="relative mt-1.5">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  data-testid="register-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="vous@exemple.com"
                  className="pl-10 h-12"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="telephone" className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                {t('register.phone', 'Téléphone')}
              </Label>
              <div className="mt-1.5 space-y-2">
                {(() => {
                  const selectedIndicatif = getIndicatifByCode(formData.indicatif_pays || '+33');
                  const phoneValidation = validatePhone(formData.telephone || '', formData.indicatif_pays || '+33');

                  return (
                    <>
                      <div className="flex gap-2">
                        <Select
                          value={formData.indicatif_pays || '+33'}
                          onValueChange={(value) => setFormData({ ...formData, indicatif_pays: value, telephone: '' })}
                        >
                          <SelectTrigger className="w-[150px] h-12">
                            <SelectValue>
                              <span className="flex items-center gap-2">
                                <img src={getFlagUrl(selectedIndicatif.flag)} alt={selectedIndicatif.name} className="w-5 h-4 object-cover rounded-sm" />
                                <span>{selectedIndicatif.code}</span>
                              </span>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            {INDICATIFS_TELEPHONIQUES.map((ind, idx) => (
                              <SelectItem key={`${ind.code}-${ind.flag}-${idx}`} value={ind.code}>
                                <span className="flex items-center gap-2">
                                  <img src={getFlagUrl(ind.flag)} alt={ind.name} className="w-5 h-4 object-cover rounded-sm" />
                                  <span>{ind.code} {ind.name}</span>
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="flex-1 relative">
                          <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                          <Input
                            id="telephone"
                            data-testid="register-telephone-input"
                            placeholder={`${phoneValidation.expectedLength} chiffres requis`}
                            value={formData.telephone}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^\d\s]/g, '');
                              setFormData({ ...formData, telephone: value });
                            }}
                            className={`pl-10 h-12 ${
                              !phoneValidation.isValid && phoneValidation.currentLength > 0
                                ? 'border-red-400 focus-visible:ring-red-300'
                                : phoneValidation.isValid && phoneValidation.currentLength > 0
                                  ? 'border-emerald-400 focus-visible:ring-emerald-300'
                                  : ''
                            }`}
                            required
                          />
                          {phoneValidation.currentLength > 0 && (
                            <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium ${
                              phoneValidation.isValid ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'
                            }`}>
                              {phoneValidation.currentLength}/{phoneValidation.expectedLength}
                            </span>
                          )}
                        </div>
                      </div>
                      {(() => {
                        if (phoneValidation.currentLength === 0) return null;
                        const diff = phoneValidation.currentLength - phoneValidation.expectedLength;
                        if (diff > 0) {
                          return (
                            <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                              <X className="w-4 h-4" /> Trop de chiffres ({diff} en trop)
                            </p>
                          );
                        } else if (diff < 0) {
                          return (
                            <p className="text-sm text-amber-600 dark:text-amber-400 flex items-center gap-1">
                              <AlertCircle className="w-4 h-4" /> {Math.abs(diff)} chiffre(s) manquant(s)
                            </p>
                          );
                        }
                        return (
                          <p className="text-sm text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" /> Numéro valide
                          </p>
                        );
                      })()}
                    </>
                  );
                })()}
              </div>
            </div>

            <div>
              <PasswordWithStrength
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
              />
            </div>

            <div className="flex gap-3 pt-1">
              <Button
                type="button"
                onClick={() => setCurrentStep(1)}
                variant="outline"
                className="flex-1 h-12 font-semibold"
                data-testid="register-step2-back-btn"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Retour
              </Button>
              <Button
                type="button"
                onClick={() => setCurrentStep(3)}
                className="flex-1 h-12 text-base font-semibold bg-gradient-to-r from-[#0B3C5D] to-[#0a1e3f] hover:from-[#0a3252] hover:to-[#091a36] shadow-lg shadow-blue-900/30 hover:shadow-xl text-white"
                data-testid="register-step2-next-btn"
              >
                Continuer
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Pro info */}
        {currentStep === 3 && (
          <div className="space-y-4 animate-in fade-in duration-300">
            {formData.role === 'beneficiaire_pro' ? (
              <div className="bg-slate-50 dark:bg-slate-800/50 p-5 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-4">
                <h3 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  {t('register.professional_info', 'Informations professionnelles')}
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                      {t('register.legal_status', 'Statut juridique')} *
                    </Label>
                    <Select value={formData.statut_juridique} onValueChange={(value) => setFormData({ ...formData, statut_juridique: value })}>
                      <SelectTrigger className="h-12 mt-1.5">
                        <SelectValue placeholder={t('register.select', 'Sélectionner')} />
                      </SelectTrigger>
                      <SelectContent>
                        {['SARL', 'SAS', 'EURL', 'SA', 'SCI', 'EI', 'MICRO', 'ASSOCIATION', 'COOPERATIVE', 'AUTRE'].map((status) => (
                          <SelectItem key={status} value={status}>
                            {status === 'EI' ? 'Entreprise Individuelle' : status === 'MICRO' ? 'Micro-entreprise' : status}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                      {t('register.naf_code', 'Code NAF')} *
                    </Label>
                    <Input
                      placeholder={t('register.naf_example', 'Ex: 6201Z')}
                      value={formData.code_naf}
                      onChange={(e) => setFormData({ ...formData, code_naf: e.target.value.toUpperCase() })}
                      className="h-12 mt-1.5"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    {t('register.territory', 'Territoire')} *
                  </Label>
                  <Select value={formData.territoire} onValueChange={(value) => setFormData({ ...formData, territoire: value })}>
                    <SelectTrigger className="h-12 mt-1.5">
                      <SelectValue placeholder={t('register.select_territory', 'Sélectionner un territoire')} />
                    </SelectTrigger>
                    <SelectContent>
                      {territoires.filter(tr => tr.nom && tr.nom.trim() !== '').map((tr) => (
                        <SelectItem key={tr.id} value={tr.nom}>{tr.nom}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
                    <Globe className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    {t('register.country', 'Pays')} *
                  </Label>
                  <Select value={formData.pays} onValueChange={(value) => setFormData({ ...formData, pays: value })}>
                    <SelectTrigger className="h-12 mt-1.5">
                      <SelectValue>
                        {(() => {
                          const pays = getPaysByCode(formData.pays);
                          return (
                            <span className="flex items-center gap-2">
                              <img src={getFlagUrl(pays.flag)} alt={pays.name} className="w-5 h-4 object-cover rounded-sm" />
                              <span>{pays.name}</span>
                            </span>
                          );
                        })()}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {PAYS_LIST.map((pays) => (
                        <SelectItem key={pays.code} value={pays.code}>
                          <span className="flex items-center gap-2">
                            <img src={getFlagUrl(pays.flag)} alt={pays.name} className="w-5 h-4 object-cover rounded-sm" />
                            <span>{pays.name}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <UnifiedActivitySelector
                  value={formData.activite_professionnelle}
                  onChange={(value) => setFormData({ ...formData, activite_professionnelle: value })}
                />
              </div>
            ) : (
              <div className="bg-slate-50 dark:bg-slate-800/50 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 text-center">
                <div className="bg-blue-100 dark:bg-blue-900/40 p-3 rounded-full w-fit mx-auto mb-3">
                  <User className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-slate-900 dark:text-white font-semibold mb-1">Compte Particulier</h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm">
                  Votre compte sera créé avec les informations fournies. Aucune information professionnelle requise.
                </p>
              </div>
            )}

            <div className="flex gap-3 pt-1">
              <Button
                type="button"
                onClick={() => setCurrentStep(2)}
                variant="outline"
                className="flex-1 h-12 font-semibold"
                data-testid="register-step3-back-btn"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Retour
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 h-12 text-base font-semibold bg-gradient-to-r from-[#0B3C5D] to-[#0a1e3f] hover:from-[#0a3252] hover:to-[#091a36] shadow-lg shadow-blue-900/30 hover:shadow-xl text-white"
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {t('register.registering', 'Inscription…')}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    {t('register.become_member', 'Devenir membre')}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </form>

      <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700 text-center">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Déjà membre ?{' '}
          <Link to="/login" className="text-blue-600 dark:text-blue-400 font-semibold hover:underline">
            Se connecter
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}
